import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareerReplace() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    axios.get(`${API}/career-replace/${token}`).then(({ data }) => setInfo(data)).catch((err) => setError(err?.response?.data?.detail || "This link is not valid."));
  }, [token]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/career-replace/${token}`, fd);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-nx-navy flex items-center justify-center p-4" data-testid="career-replace-page">
      <div className="w-full max-w-md border border-white/15 bg-nx-navy2 p-6 sm:p-8">
        <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">NexSecure Technologies · Careers</p>
            <h1 className="font-marcellus text-2xl text-white mt-2">{info?.action === "rejected" ? "Document Rejected" : "Document Replacement"}</h1>
        {error ? (
          <p className="text-sm text-red-300 mt-5 flex items-start gap-2" data-testid="career-replace-error"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {error}</p>
        ) : !info ? (
          <p className="text-sm text-slate-400 mt-5">Loading…</p>
        ) : done ? (
          <div className="mt-5" data-testid="career-replace-done">
            <p className="text-sm text-emerald-300 flex items-center gap-2"><CheckCircle2 size={16} /> Your {info.label} has been replaced. Thank you.</p>
            <p className="text-xs text-slate-500 mt-2">Our HR team will review the new document. You can close this page.</p>
          </div>
        ) : (
          <div className="mt-5">
            <p className="text-sm text-slate-300 leading-relaxed">Dear {info.name}, HR has {info.action === "rejected" ? "rejected" : "asked you to re-upload"} your <b className="text-white">{info.label}</b> for application <b className="text-white">{info.reference_number}</b>. Only this document can be replaced here.</p>
            {info.action === "rejected" && info.reason && <p className="text-sm text-red-300 mt-3 border border-red-500/30 bg-red-500/5 p-3" data-testid="career-replace-reason"><b>Reason:</b> {info.reason}</p>}
            <p className="text-[11px] text-slate-500 mt-3">Accepted: {info.accept.map((a) => a.toUpperCase()).join(", ")}</p>
            <input ref={ref} type="file" className="hidden" accept={info.accept.map((a) => `.${a}`).join(",")} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="career-replace-input" />
            <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="mt-5 inline-flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3.5 transition-colors disabled:opacity-60" data-testid="career-replace-upload">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? "Uploading…" : `Upload new ${info.label}`}
            </button>
            <p className="text-[11px] text-slate-600 mt-4">Questions? Contact hr@nexsecure.in</p>
          </div>
        )}
      </div>
    </div>
  );
}
